import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { TextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { bookOpen_01 } from "@tql/ui-components-react/untitled/icons";

import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import type { SubGroupKey } from "~/shared/types/quote-details-types";

import styles from "./tender-pickup-delivery-details.module.css";

export const ADDRESS_MAX_LENGTH = 50;

type TenderAddressLinesProps = {
	type: SubGroupKey;
	hasFieldError: (field: string) => boolean;
	onClearFieldError: (field: string) => void;
	/** Mobile only: renders an "Address Book" link next to the Address Line 1 label. */
	showAddressBookButton?: boolean;
	addressBookDisabled?: boolean;
	onAddressBookClick?: () => void;
};

export const TenderAddressLines = ({
	type,
	hasFieldError,
	onClearFieldError,
	showAddressBookButton = false,
	addressBookDisabled = false,
	onAddressBookClick,
}: Readonly<TenderAddressLinesProps>) => {
	const { selectedAddressBookItems, updateSelectedAddressBookItem } =
		useQuoteAddressContext();
	const addressBookItem = selectedAddressBookItems[type];

	const address1 = addressBookItem?.address1 ?? "";
	const isAddress1OverLimit = address1.length > ADDRESS_MAX_LENGTH;
	const showAddress1Error =
		isAddress1OverLimit || hasFieldError(`${type}Address1`);
	const address1ErrorMessage = isAddress1OverLimit
		? "Cannot exceed character limit."
		: "Required";
	const address2 = addressBookItem?.address2 ?? "";
	const isAddress2OverLimit = address2.length > ADDRESS_MAX_LENGTH;

	return (
		<>
			<div>
				<div className={styles.addressLine1LabelRow}>
					<Label editorId={`${type}Address1`}>
						Address Line 1 <span className={"required"}>*</span>
					</Label>
					{showAddressBookButton ? (
						<Button
							disabled={addressBookDisabled}
							onClick={onAddressBookClick}
							svgIcon={bookOpen_01}
							tabIndex={-1}
							variant={"tertiary"}
						>
							Address Book
						</Button>
					) : null}
				</div>
				<TextBox
					id={`${type}Address1`}
					name={`${type}Address1`}
					onChange={(e) => {
						onClearFieldError(`${type}Address1`);
						onClearFieldError(`${type}Address1Length`);
						updateSelectedAddressBookItem(
							type,
							"address1",
							e.target.value as string,
						);
					}}
					valid={!showAddress1Error}
					value={address1}
				/>
				<div
					className={styles.fieldMessages}
					data-testid={`${type}-address-1-messages`}
				>
					{showAddress1Error ? <Error>{address1ErrorMessage}</Error> : <span />}
					{address1.length > 0 ? (
						<span
							className={`${styles.charCounter} ${
								isAddress1OverLimit ? styles.charCounterOverLimit : ""
							}`}
						>
							{address1.length}/{ADDRESS_MAX_LENGTH}
						</span>
					) : null}
				</div>
			</div>
			<div>
				<Label editorId={`${type}Address2`}>Address Line 2</Label>
				<TextBox
					id={`${type}Address2`}
					name={`${type}Address2`}
					onChange={(e) => {
						onClearFieldError(`${type}Address2Length`);
						updateSelectedAddressBookItem(
							type,
							"address2",
							e.target.value as string,
						);
					}}
					valid={!isAddress2OverLimit}
					value={address2}
				/>
				<div className={styles.fieldMessages}>
					{isAddress2OverLimit ? (
						<Error>Cannot exceed character limit.</Error>
					) : (
						<span />
					)}
					{address2.length > 0 ? (
						<span
							className={`${styles.charCounter} ${
								isAddress2OverLimit ? styles.charCounterOverLimit : ""
							}`}
						>
							{address2.length}/{ADDRESS_MAX_LENGTH}
						</span>
					) : null}
				</div>
			</div>
		</>
	);
};
